import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { ZAR } from "@/lib/cart";
import AdminGuide from "@/components/admin/AdminGuide";
import AnalyticsAdmin from "@/components/admin/AnalyticsAdmin";
import { ChartLine, Package, FileText, Tag, Receipt, Envelope, UsersThree, Gift, BookOpen, Download, UploadSimple, Plus, Trash, PencilSimple, X, Image as ImageIcon, CloudArrowUp, Eye } from "@phosphor-icons/react";

const TABS = [
  { id: "analytics", label: "Analytics", Icon: ChartLine, testId: "admin-tab-analytics" },
  { id: "products", label: "Products", Icon: Package, testId: ADMIN.tabProducts },
  { id: "content", label: "Site Content", Icon: FileText, testId: ADMIN.tabContent },
  { id: "discounts", label: "Discounts", Icon: Tag, testId: ADMIN.tabDiscounts },
  { id: "orders", label: "Orders", Icon: Receipt, testId: ADMIN.tabOrders },
  { id: "newsletter", label: "Newsletter", Icon: Envelope, testId: ADMIN.tabNewsletter },
  { id: "referrals", label: "Referrals", Icon: UsersThree, testId: "admin-tab-referrals" },
  { id: "bundles", label: "Bundles", Icon: Gift, testId: "admin-tab-bundles" },
  { id: "guide", label: "Guide", Icon: BookOpen, testId: "admin-tab-guide" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("analytics");
  return (
    <div data-testid={ADMIN.dashboard} className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-16 lg:px-10">
      <div className="font-label text-xs text-[#FF5A1F]">/ ADMIN CONTROL</div>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-5xl">Dashboard</h1>

      <div className="mt-6 sm:mt-10 -mx-4 sm:mx-0 overflow-x-auto border-b border-white/10 pb-3">
        <div className="flex flex-nowrap sm:flex-wrap gap-2 px-4 sm:px-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testId}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 border px-3 py-2 font-label text-[11px] transition-colors whitespace-nowrap ${
                tab === t.id
                  ? "border-[#FF5A1F] bg-[#FF5A1F] text-black"
                  : "border-white/15 hover:border-white/40"
              }`}
            >
              <t.Icon size={12} weight="bold" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 sm:mt-10">
        {tab === "analytics" && <AnalyticsAdmin />}
        {tab === "products" && <ProductsAdmin />}
        {tab === "content" && <ContentAdmin />}
        {tab === "discounts" && <DiscountsAdmin />}
        {tab === "orders" && <OrdersAdmin />}
        {tab === "newsletter" && <NewsletterAdmin />}
        {tab === "referrals" && <ReferralsAdmin />}
        {tab === "bundles" && <BundlesAdmin />}
        {tab === "guide" && <AdminGuide />}
      </div>
    </div>
  );
}

/* ----- Products (CRUD + Excel) ----- */
function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/products/admin/template", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vape_essence_products_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("Template downloaded");
    } catch (err) {
      console.error("Template download failed", err);
      toast.error("Failed to download template — check you're signed in as admin");
    }
  };

  const bulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/admin/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Created ${data.created}, updated ${data.updated}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows had errors`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const del = async (slug) => {
    if (!confirm(`Delete ${slug}?`)) return;
    await api.delete(`/products/${slug}`);
    toast.success("Deleted");
    load();
  };

  const seedCatalog = async () => {
    if (!confirm(
      "This will:\n" +
      "  • DELETE every product whose name or slug contains 'Example' or 'TEST'\n" +
      "  • ADD the real 84-product Vape Essence catalogue (skips any that already exist)\n\n" +
      "Continue?"
    )) return;
    try {
      const { data } = await api.post("/products/admin/seed-catalog");
      toast.success(
        `Removed ${data.removed_count} test items · Inserted ${data.inserted_count} · Skipped ${data.skipped_count} (already existed)`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Seed failed");
    }
  };

  const repairSlugs = async () => {
    try {
      const { data } = await api.post("/products/admin/repair-slugs");
      if (data.fixed?.length) {
        toast.success(`Repaired ${data.fixed.length} product slug(s)`);
        load();
      } else {
        toast.success("All slugs are healthy — nothing to fix");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Repair failed");
    }
  };

  const applyFeaturedPicks = async () => {
    if (!confirm(
      "This will feature 8 curated hero products on the homepage " +
      "(and un-feature every other product). Continue?"
    )) return;
    try {
      const { data } = await api.post("/products/admin/apply-featured-picks");
      const missing = data.missing?.length
        ? ` · ${data.missing.length} pick(s) not yet in catalogue`
        : "";
      toast.success(`Featured ${data.featured_count} hero product(s)${missing}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to apply featured picks");
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button
          data-testid={ADMIN.productAddBtn}
          onClick={() =>
            setEditing({
              slug: "",
              name: "",
              brand: "",
              category: "Disposable Vapes",
              price: 0,
              compare_at: null,
              description: "",
              images: [],
              featured: false,
              stock: 0,
              tags: [],
              variant_options: [],
              variants: [],
            })
          }
          className="inline-flex items-center gap-2 bg-[#FF5A1F] px-4 py-2 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
        >
          <Plus size={14} weight="bold" /> New product
        </button>
        <button
          data-testid={ADMIN.templateDownload}
          onClick={downloadTemplate}
          className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-label text-xs hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
        >
          <Download size={14} weight="bold" /> Excel template
        </button>
        <label
          data-testid={ADMIN.bulkUploadBtn}
          className="inline-flex items-center gap-2 border border-[#00FFC2] bg-[#00FFC2] px-4 py-2 font-label text-xs text-black hover:-translate-y-0.5 transition-transform cursor-pointer"
        >
          <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading..." : "Bulk upload"}
          <input
            data-testid={ADMIN.bulkUploadInput}
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={bulkUpload}
          />
        </label>
        <button
          data-testid="admin-seed-catalog-btn"
          onClick={seedCatalog}
          className="inline-flex items-center gap-2 border border-[#FFC900] px-4 py-2 font-label text-xs text-[#FFC900] hover:bg-[#FFC900] hover:text-black transition-colors"
          title="Remove Example/TEST products and add the real 84-product Vape Essence catalogue"
        >
          <Package size={14} weight="bold" /> Seed real catalogue
        </button>
        <button
          data-testid="admin-featured-picks-btn"
          onClick={applyFeaturedPicks}
          className="inline-flex items-center gap-2 border border-[#00FFC2] px-4 py-2 font-label text-xs text-[#00FFC2] hover:bg-[#00FFC2] hover:text-black transition-colors"
          title="Mark 8 curated hero products as Featured so the homepage carousel is populated"
        >
          <Gift size={14} weight="bold" /> Feature hero picks
        </button>
        <button
          data-testid="admin-repair-slugs-btn"
          onClick={repairSlugs}
          className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 font-label text-[10px] text-white/70 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
          title="Auto-fix any products whose URL slug is broken or missing"
        >
          Fix broken slugs
        </button>
        <span className="ml-auto text-xs text-white/40">{products.length} products</span>
      </div>

      {editing && (
        <ProductEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {(() => {
        const missing = products.filter((p) => p.featured && !(p.images || []).length);
        if (missing.length === 0) return null;
        return (
          <div
            data-testid="admin-featured-missing-alert"
            className="mb-4 border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-3 text-xs text-[#FF5A1F]"
          >
            ⚠️ <b>{missing.length}</b> featured product{missing.length > 1 ? "s" : ""} still need photos —
            homepage art will look weak until images are uploaded.{" "}
            <span className="text-white/70">Upload via the drag-and-drop uploader when editing each product.</span>
          </div>
        );
      })()}

      <div className="border border-white/10">
        <div className="grid grid-cols-12 gap-2 border-b border-white/10 bg-black px-4 py-3 font-label text-[10px] text-white/40">
          <div className="col-span-1">IMG</div>
          <div className="col-span-4">NAME</div>
          <div className="col-span-2">CATEGORY</div>
          <div className="col-span-2">PRICE</div>
          <div className="col-span-1">STOCK</div>
          <div className="col-span-2 text-right">ACTIONS</div>
        </div>
        {products.map((p) => {
          const hasImg = (p.images || []).length > 0;
          const needsPhoto = p.featured && !hasImg;
          return (
          <div
            key={p.slug}
            data-testid={`admin-product-row-${p.slug}`}
            className={`grid grid-cols-12 items-center gap-2 border-b border-white/10 px-4 py-3 last:border-b-0 ${
              needsPhoto ? "bg-[#FF5A1F]/5" : ""
            }`}
          >
            <div className="col-span-1">
              {hasImg ? (
                <img src={mediaUrl(p.images[0])} alt="" className="h-10 w-10 object-cover border border-white/10" />
              ) : (
                <div
                  data-testid={`admin-no-image-${p.slug}`}
                  title={needsPhoto ? "Featured — needs a photo!" : "No image yet"}
                  className={`flex h-10 w-10 items-center justify-center border ${
                    needsPhoto ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-white/10 text-white/30"
                  }`}
                >
                  <ImageIcon size={14} />
                </div>
              )}
            </div>
            <div className="col-span-4 truncate">
              <div className="flex items-center gap-2">
                <div className="font-display font-bold text-sm truncate">{p.name}</div>
                {p.featured && (
                  <span
                    data-testid={`admin-featured-tag-${p.slug}`}
                    className="shrink-0 border border-[#00FFC2]/40 bg-[#00FFC2]/10 px-1.5 py-0.5 font-label text-[8px] tracking-widest text-[#00FFC2]"
                  >
                    FEATURED
                  </span>
                )}
                {needsPhoto && (
                  <span
                    data-testid={`admin-needs-photo-${p.slug}`}
                    className="shrink-0 border border-[#FF5A1F]/60 bg-[#FF5A1F]/10 px-1.5 py-0.5 font-label text-[8px] tracking-widest text-[#FF5A1F] animate-pulse"
                    title="This product is Featured but has no photo — homepage art will look weak"
                  >
                    NEEDS PHOTO
                  </span>
                )}
              </div>
              <div className="text-[11px] text-white/40">{p.slug}</div>
            </div>
            <div className="col-span-2 text-xs text-white/60">{p.category}</div>
            <div className="col-span-2 text-xs text-[#00FFC2]">{ZAR(p.price)}</div>
            <div className="col-span-1 text-xs">{p.stock}</div>
            <div className="col-span-2 flex justify-end gap-2">
              {p.slug && (
                <a
                  href={`/product/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`admin-product-view-${p.slug}`}
                  title="Open on storefront"
                  className="text-white/50 hover:text-[#FFC900] transition-colors"
                >
                  <Eye size={16} />
                </a>
              )}
              <button
                onClick={() => setEditing(p)}
                className="text-white/50 hover:text-[#00FFC2] transition-colors"
              >
                <PencilSimple size={16} />
              </button>
              <button
                data-testid={ADMIN.productDeleteBtn}
                onClick={() => del(p.slug)}
                className="text-white/50 hover:text-[#FF5A1F] transition-colors"
              >
                <Trash size={16} />
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function ProductEditor({ initial, onClose, onSaved }) {
  const [p, setP] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const isNew = !initial.slug || !initial.name;
  // In new-product mode, keep slug auto-synced to the name until the admin
  // manually types in the slug field.
  const [slugTouched, setSlugTouched] = useState(!isNew);

  const setField = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const setName = (v) => {
    setP((prev) => {
      const next = { ...prev, name: v };
      if (!slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const setSlug = (v) => {
    setSlugTouched(true);
    setP((prev) => ({ ...prev, slug: v }));
  };

  const addOption = () =>
    setP({ ...p, variant_options: [...(p.variant_options || []), { name: "Flavour", values: [] }] });

  const removeOption = (i) =>
    setP({ ...p, variant_options: p.variant_options.filter((_, idx) => idx !== i) });

  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const reorderImage = (from, to) => {
    if (from === null || to === null || from === to) return;
    const list = [...(p.images || [])];
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setField("images", list);
  };

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f && f.type && f.type.startsWith("image/"));
    if (list.length === 0) {
      toast.error("No valid image files selected");
      return;
    }
    // Guard: token must be present in localStorage
    const token = localStorage.getItem("ve_token");
    if (!token) {
      toast.error("Session expired — please sign in as admin again");
      return;
    }
    setUploadingImg(true);
    const uploaded = [];
    for (const file of list) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB — skipped`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        // Do NOT set Content-Type manually — let the browser set the multipart boundary.
        const { data } = await api.post("/products/upload-image", fd);
        uploaded.push(data.url);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401 || status === 403) {
          toast.error("Not authorised — please sign in as admin again");
        } else {
          toast.error(`Upload failed${detail ? ": " + detail : ""}`);
        }
      }
    }
    if (uploaded.length > 0) {
      setP((prev) => ({ ...prev, images: [...(prev.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    }
    setUploadingImg(false);
  };

  const onFileInput = (e) => {
    uploadFiles(e.target.files);
    e.target.value = ""; // allow re-selecting same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    uploadFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const save = async () => {
    // Guard against saving an unusable product that produces an unreachable URL.
    const finalSlug = slugify(p.slug || p.name || "");
    const finalName = (p.name || "").trim();
    if (!finalName) {
      toast.error("Please enter a product name before saving");
      return;
    }
    if (!finalSlug) {
      toast.error("Slug cannot be empty — set a product name or slug");
      return;
    }
    if (!p.brand?.trim() || !p.category?.trim()) {
      toast.error("Brand and Category are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...p,
      slug: finalSlug,
      name: finalName,
      price: Number(p.price) || 0,
      compare_at: p.compare_at ? Number(p.compare_at) : null,
      stock: Number(p.stock) || 0,
      variant_options: (p.variant_options || []).map((o) => ({
        name: o.name,
        values: typeof o.values === "string" ? o.values.split(",").map((v) => v.trim()).filter(Boolean) : o.values,
      })),
    };
    try {
      if (isNew) {
        await api.post("/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/products/${initial.slug}`, payload);
        toast.success("Product saved");
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : detail || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 backdrop-blur">
      <div className="flex min-h-full items-start sm:items-center justify-center p-4">
      <div className="w-full max-w-3xl border border-white/10 bg-[#0d0d0d] p-8 my-8" data-testid="admin-product-modal">
        <div className="mb-6 flex items-center justify-between">
          <div className="font-display text-2xl font-black">{isNew ? "New product" : "Edit product"}</div>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label={isNew ? "Slug (auto from name — edit to override)" : "Slug (unique)"}>
            <Input
              value={p.slug}
              disabled={!isNew}
              onChange={setSlug}
              data-testid="admin-product-slug-input"
            />
          </Field>
          <Field label="Name">
            <Input value={p.name} onChange={setName} data-testid="admin-product-name-input" />
          </Field>
          <Field label="Brand"><Input value={p.brand} onChange={(v) => setField("brand", v)} /></Field>
          <Field label="Category"><Input value={p.category} onChange={(v) => setField("category", v)} /></Field>
          <Field label="Price (R)"><Input type="number" value={p.price} onChange={(v) => setField("price", v)} /></Field>
          <Field label="Compare at"><Input type="number" value={p.compare_at || ""} onChange={(v) => setField("compare_at", v)} /></Field>
          <Field label="Stock"><Input type="number" value={p.stock} onChange={(v) => setField("stock", v)} /></Field>
          <Field label="Featured">
            <select
              value={p.featured ? "1" : "0"}
              onChange={(e) => setField("featured", e.target.value === "1")}
              className="w-full border border-white/15 bg-black px-3 py-2 text-sm"
            >
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </Field>
        </div>

        {isNew && (
          <div className="-mt-1 mb-2 text-[11px] text-white/50">
            Public URL will be:{" "}
            <span className="font-mono text-[#00FFC2]">
              /product/{p.slug || <span className="text-[#FF5A1F]">— add a name first —</span>}
            </span>
          </div>
        )}

        <Field label="Description">
          <textarea
            value={p.description}
            onChange={(e) => setField("description", e.target.value)}
            rows={3}
            className="w-full border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-[#FF5A1F]"
          />
        </Field>

        <Field label="Tags (comma-sep)">
          <Input
            value={(p.tags || []).join(", ")}
            onChange={(v) => setField("tags", v.split(",").map((t) => t.trim()).filter(Boolean))}
          />
        </Field>

        <Field label="Images">
          <div
            data-testid="admin-image-dropzone"
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer border-2 border-dashed p-6 text-center transition-colors ${
              dragActive ? "border-[#00FFC2] bg-[#00FFC2]/5" : "border-white/20 hover:border-[#00FFC2]/70 bg-black/40"
            }`}
          >
            <input
              ref={fileInputRef}
              data-testid="admin-image-file-input"
              type="file"
              accept="image/*"
              multiple
              onChange={onFileInput}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-2 text-white/70">
              <CloudArrowUp size={32} weight="duotone" className="text-[#00FFC2]" />
              <div className="text-sm">
                {uploadingImg ? (
                  <span className="text-[#00FFC2]">Uploading…</span>
                ) : (
                  <>
                    <b className="text-white">Drag &amp; drop images here</b>{" "}
                    <span className="text-white/50">or click to browse</span>
                  </>
                )}
              </div>
              <div className="text-[10px] font-label tracking-widest text-white/40">
                PNG · JPG · WEBP · GIF · MAX 10MB EACH
              </div>
            </div>
          </div>

          {(p.images || []).length > 0 && (
            <>
              <div className="mt-3 text-[10px] font-label tracking-widest text-white/40">
                DRAG THUMBNAILS TO REORDER · FIRST IMAGE IS THE COVER
              </div>
              <div className="mt-2 flex flex-wrap gap-3">
                {(p.images || []).map((im, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={(e) => {
                      setDragIndex(i);
                      e.dataTransfer.effectAllowed = "move";
                      // Prevent the outer dropzone from also handling this drag
                      e.stopPropagation();
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (dragOverIndex !== i) setDragOverIndex(i);
                    }}
                    onDragLeave={(e) => {
                      e.stopPropagation();
                      if (dragOverIndex === i) setDragOverIndex(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      reorderImage(dragIndex, i);
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    data-testid={`admin-image-thumb-${i}`}
                    className={`relative group cursor-grab active:cursor-grabbing transition-all ${
                      dragOverIndex === i ? "ring-2 ring-[#00FFC2] scale-105" : ""
                    } ${dragIndex === i ? "opacity-40" : ""}`}
                  >
                    <img src={mediaUrl(im)} alt="" className="h-24 w-24 object-cover border border-white/15" />
                    <button
                      type="button"
                      data-testid={`admin-image-remove-${i}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setField("images", p.images.filter((_, x) => x !== i));
                      }}
                      className="absolute -right-2 -top-2 bg-[#FF5A1F] text-black rounded-full h-6 w-6 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                      aria-label="Remove image"
                    >
                      <X size={12} weight="bold" />
                    </button>
                    {i === 0 && (
                      <div className="absolute bottom-0 inset-x-0 bg-black/70 px-1 py-0.5 text-center font-label text-[8px] tracking-widest text-[#00FFC2]">
                        COVER
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </Field>

        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-label text-xs text-white/50">Variant options</div>
            <button
              onClick={addOption}
              className="text-xs font-label text-[#00FFC2] hover:underline"
            >
              + Add option
            </button>
          </div>
          {(p.variant_options || []).map((o, i) => (
            <div key={i} className="mt-2 grid grid-cols-12 gap-2">
              <input
                value={o.name}
                onChange={(e) => {
                  const opts = [...p.variant_options];
                  opts[i] = { ...opts[i], name: e.target.value };
                  setField("variant_options", opts);
                }}
                placeholder="Flavour / Colour / Strength"
                className="col-span-3 border border-white/15 bg-black px-3 py-2 text-sm"
              />
              <input
                value={Array.isArray(o.values) ? o.values.join(", ") : o.values || ""}
                onChange={(e) => {
                  const opts = [...p.variant_options];
                  opts[i] = { ...opts[i], values: e.target.value };
                  setField("variant_options", opts);
                }}
                placeholder="Mint, Berry, Cola"
                className="col-span-8 border border-white/15 bg-black px-3 py-2 text-sm"
              />
              <button
                onClick={() => removeOption(i)}
                className="col-span-1 text-white/40 hover:text-[#FF5A1F]"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end gap-2">
          <button onClick={onClose} className="border border-white/20 px-4 py-2 font-label text-xs">
            Cancel
          </button>
          <button
            data-testid={ADMIN.productSaveBtn}
            onClick={save}
            disabled={saving}
            className="bg-[#FF5A1F] px-6 py-2 font-label text-xs text-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save product"}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="mb-1 font-label text-[10px] text-white/40">{label}</div>
    {children}
  </label>
);
const Input = ({ value, onChange, type = "text", disabled = false, "data-testid": testId }) => (
  <input
    disabled={disabled}
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="w-full border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-[#FF5A1F] disabled:opacity-60"
  />
);

/* ----- Site Content ----- */
function ContentAdmin() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setC(r.data));
  }, []);

  if (!c) return <div className="text-white/40">Loading...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", c);
      toast.success("Site content saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setPillar = (i, k, v) => {
    const list = [...(c.about_pillars || [])];
    list[i] = { ...list[i], [k]: v };
    setC({ ...c, about_pillars: list });
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Hero tagline"><Input value={c.hero_tagline} onChange={(v) => setC({ ...c, hero_tagline: v })} /></Field>
        <Field label="Hero title"><Input value={c.hero_title} onChange={(v) => setC({ ...c, hero_title: v })} /></Field>
      </div>
      <Field label="Hero subtitle">
        <textarea
          value={c.hero_subtitle}
          onChange={(e) => setC({ ...c, hero_subtitle: e.target.value })}
          rows={2}
          className="w-full border border-white/15 bg-black px-3 py-2 text-sm"
        />
      </Field>

      <Field label="About title"><Input value={c.about_title} onChange={(v) => setC({ ...c, about_title: v })} /></Field>
      <Field label="About body">
        <textarea
          value={c.about_body}
          onChange={(e) => setC({ ...c, about_body: e.target.value })}
          rows={5}
          className="w-full border border-white/15 bg-black px-3 py-2 text-sm"
        />
      </Field>

      <div>
        <div className="font-label text-xs text-white/50 mb-2">About pillars</div>
        {(c.about_pillars || []).map((p, i) => (
          <div key={i} className="mb-2 grid grid-cols-12 gap-2">
            <input
              value={p.title}
              onChange={(e) => setPillar(i, "title", e.target.value)}
              className="col-span-4 border border-white/15 bg-black px-3 py-2 text-sm"
            />
            <input
              value={p.body}
              onChange={(e) => setPillar(i, "body", e.target.value)}
              className="col-span-8 border border-white/15 bg-black px-3 py-2 text-sm"
            />
          </div>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Contact email"><Input value={c.contact_email} onChange={(v) => setC({ ...c, contact_email: v })} /></Field>
        <Field label="Contact phone"><Input value={c.contact_phone} onChange={(v) => setC({ ...c, contact_phone: v })} /></Field>
        <Field label="Contact address"><Input value={c.contact_address} onChange={(v) => setC({ ...c, contact_address: v })} /></Field>
      </div>
      <Field label="Footer tagline">
        <Input value={c.footer_tagline} onChange={(v) => setC({ ...c, footer_tagline: v })} />
      </Field>

      <Field label="Free shipping threshold (R)">
        <Input
          type="number"
          value={c.free_shipping_threshold || 500}
          onChange={(v) => setC({ ...c, free_shipping_threshold: Number(v) || 0 })}
        />
      </Field>

      <Field label="TikTok video URLs (one per line)">
        <textarea
          value={(c.tiktok_urls || []).join("\n")}
          onChange={(e) =>
            setC({
              ...c,
              tiktok_urls: e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean),
            })
          }
          rows={4}
          placeholder="https://www.tiktok.com/@yourhandle/video/1234567890"
          className="w-full border border-white/15 bg-black px-3 py-2 text-sm font-mono"
        />
      </Field>

      <div className="border-t border-white/10 pt-6">
        <div className="font-label text-[10px] text-[#00FFC2] mb-3">/ MARKETING & ANALYTICS</div>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Google Analytics 4 Measurement ID">
            <Input
              value={c.ga_measurement_id || ""}
              onChange={(v) => setC({ ...c, ga_measurement_id: v })}
              placeholder="G-XXXXXXXXXX"
            />
          </Field>
          <Field label="Meta (Facebook) Pixel ID">
            <Input
              value={c.meta_pixel_id || ""}
              onChange={(v) => setC({ ...c, meta_pixel_id: v })}
              placeholder="1234567890123456"
            />
          </Field>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="flex items-center gap-3 text-sm text-white/80">
            <input
              type="checkbox"
              checked={c.exit_intent_enabled !== false}
              onChange={(e) => setC({ ...c, exit_intent_enabled: e.target.checked })}
              className="h-4 w-4 accent-[#FF5A1F]"
            />
            Show exit-intent discount popup
          </label>
          <label className="flex items-center gap-3 text-sm text-white/80">
            <input
              type="checkbox"
              checked={c.social_proof_enabled !== false}
              onChange={(e) => setC({ ...c, social_proof_enabled: e.target.checked })}
              className="h-4 w-4 accent-[#FF5A1F]"
            />
            Show live social-proof toasts
          </label>
        </div>
        <div className="mt-3 text-[11px] text-white/40">
          Paste the IDs above and both trackers load automatically on every page. Leave blank to disable.
        </div>
      </div>

      <button
        data-testid={ADMIN.contentSaveBtn}
        onClick={save}
        disabled={saving}
        className="bg-[#FF5A1F] px-6 py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save all content"}
      </button>
    </div>
  );
}

/* ----- Discounts ----- */
function DiscountsAdmin() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: "", amount_off: "", active: true, description: "" });

  const load = () => api.get("/discounts").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code) return toast.error("Code required");
    try {
      await api.post("/discounts", {
        ...form,
        percent_off: form.percent_off ? Number(form.percent_off) : null,
        amount_off: form.amount_off ? Number(form.amount_off) : null,
      });
      toast.success("Saved");
      setForm({ code: "", percent_off: "", amount_off: "", active: true, description: "" });
      load();
    } catch {
      toast.error("Failed");
    }
  };
  const del = async (code) => {
    await api.delete(`/discounts/${code}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="border border-white/10 bg-[#0d0d0d] p-6">
        <div className="font-label text-xs text-[#00FFC2] mb-4">/ NEW / UPDATE CODE</div>
        <div className="space-y-3">
          <Field label="Code"><Input value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="% off"><Input type="number" value={form.percent_off} onChange={(v) => setForm({ ...form, percent_off: v })} /></Field>
            <Field label="R amount off"><Input type="number" value={form.amount_off} onChange={(v) => setForm({ ...form, amount_off: v })} /></Field>
          </div>
          <Field label="Description"><Input value={form.description} onChange={(v) => setForm({ ...form, description: v })} /></Field>
          <button
            data-testid={ADMIN.discountSave}
            onClick={save}
            className="w-full bg-[#00FFC2] py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
          >
            Save discount
          </button>
        </div>
      </div>

      <div className="border border-white/10">
        <div className="grid grid-cols-4 gap-2 border-b border-white/10 bg-black px-4 py-3 font-label text-[10px] text-white/40">
          <div>CODE</div>
          <div>VALUE</div>
          <div>ACTIVE</div>
          <div></div>
        </div>
        {list.map((d) => (
          <div key={d.code} className="grid grid-cols-4 items-center gap-2 border-b border-white/10 px-4 py-3 last:border-b-0">
            <div className="font-display font-bold">{d.code}</div>
            <div className="text-xs text-[#FFC900]">
              {d.percent_off ? `${d.percent_off}%` : d.amount_off ? ZAR(d.amount_off) : "-"}
            </div>
            <div className="text-xs">{d.active ? "Yes" : "No"}</div>
            <div className="text-right">
              <button onClick={() => del(d.code)} className="text-white/40 hover:text-[#FF5A1F]">
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----- Orders ----- */
function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState({});
  const load = () => api.get("/orders/admin/all").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/orders/admin/${id}`, { status });
    toast.success("Status updated");
    load();
  };

  const saveTracking = async (id) => {
    const t = editing[id] || {};
    const payload = {};
    if (t.tracking_number != null) payload.tracking_number = t.tracking_number;
    if (t.courier != null) payload.courier = t.courier;
    if (t.tracking_url != null) payload.tracking_url = t.tracking_url;
    if (!Object.keys(payload).length) return;
    await api.patch(`/orders/admin/${id}`, payload);
    toast.success("Tracking saved");
    setEditing((prev) => ({ ...prev, [id]: undefined }));
    load();
  };

  return (
    <div className="border border-white/10">
      <div className="hidden md:grid grid-cols-6 gap-2 border-b border-white/10 bg-black px-4 py-3 font-label text-[10px] text-white/40">
        <div>ORDER</div>
        <div>DATE</div>
        <div>CUSTOMER</div>
        <div>TOTAL</div>
        <div>STATUS</div>
        <div className="text-right">SET</div>
      </div>
      {orders.length === 0 && <div className="p-8 text-center text-sm text-white/40">No orders yet.</div>}
      {orders.map((o) => {
        const t = editing[o.order_id] ?? {
          tracking_number: o.tracking_number || "",
          courier: o.courier || "",
          tracking_url: o.tracking_url || "",
        };
        const showTracking = ["paid", "shipped", "delivered"].includes(o.status);
        return (
          <div key={o.order_id} className="border-b border-white/10 last:border-b-0">
            <div className="grid grid-cols-2 md:grid-cols-6 items-center gap-2 px-4 py-3 text-xs">
              <div className="font-mono truncate">{o.order_id}</div>
              <div className="text-white/50 hidden md:block">{new Date(o.created_at).toLocaleDateString()}</div>
              <div className="truncate hidden md:block">{o.customer_email}</div>
              <div className="text-[#00FFC2]">{ZAR(o.amount)}</div>
              <div>
                <span
                  className={`inline-block border px-2 py-1 font-label ${
                    o.status === "paid"
                      ? "border-[#00FFC2] text-[#00FFC2]"
                      : o.status === "shipped" || o.status === "delivered"
                      ? "border-[#FFC900] text-[#FFC900]"
                      : "border-white/20 text-white/60"
                  }`}
                >
                  {o.status}
                </span>
              </div>
              <div className="text-right col-span-2 md:col-span-1">
                <select
                  value={o.status}
                  onChange={(e) => setStatus(o.order_id, e.target.value)}
                  className="border border-white/15 bg-black px-2 py-1 text-xs"
                >
                  {["pending", "paid", "shipped", "delivered", "cancelled", "failed"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tracking editor — visible for paid+ orders */}
            {showTracking && (
              <div className="border-t border-white/5 bg-black/30 px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-label text-[9px] text-white/40">TRACKING</div>
                  {o.tracking_number && (
                    <span className="text-[10px] text-[#00FFC2]">
                      Live · shown on /track
                    </span>
                  )}
                </div>
                <div className="grid gap-2 md:grid-cols-[1fr_1fr_1.5fr_auto]">
                  <input
                    data-testid={`admin-tracking-number-${o.order_id}`}
                    placeholder="Tracking number"
                    value={t.tracking_number}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, [o.order_id]: { ...t, tracking_number: e.target.value } }))
                    }
                    className="border border-white/15 bg-black px-3 py-2 text-xs outline-none focus:border-[#FF5A1F]"
                  />
                  <input
                    placeholder="Courier (Aramex, DHL, PostNet…)"
                    value={t.courier}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, [o.order_id]: { ...t, courier: e.target.value } }))
                    }
                    className="border border-white/15 bg-black px-3 py-2 text-xs outline-none focus:border-[#FF5A1F]"
                  />
                  <input
                    placeholder="Live tracking URL (optional)"
                    value={t.tracking_url}
                    onChange={(e) =>
                      setEditing((p) => ({ ...p, [o.order_id]: { ...t, tracking_url: e.target.value } }))
                    }
                    className="border border-white/15 bg-black px-3 py-2 text-xs outline-none focus:border-[#FF5A1F]"
                  />
                  <button
                    onClick={() => saveTracking(o.order_id)}
                    data-testid={`admin-tracking-save-${o.order_id}`}
                    className="bg-[#FF5A1F] px-3 py-2 font-label text-[10px] text-black hover:-translate-y-0.5 transition-transform"
                  >
                    SAVE
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ----- Newsletter ----- */
function NewsletterAdmin() {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    api.get("/newsletter/admin/list").then((r) => setSubs(r.data));
  }, []);
  return (
    <div className="border border-white/10">
      <div className="grid grid-cols-3 gap-2 border-b border-white/10 bg-black px-4 py-3 font-label text-[10px] text-white/40">
        <div>EMAIL</div>
        <div>REFERRED BY</div>
        <div>WHEN</div>
      </div>
      {subs.length === 0 && <div className="p-8 text-center text-sm text-white/40">No subscribers yet.</div>}
      {subs.map((s, i) => (
        <div key={i} className="grid grid-cols-3 gap-2 border-b border-white/10 px-4 py-3 last:border-b-0 text-xs">
          <div>{s.email}</div>
          <div className="text-white/50">{s.referrer_email || "-"}</div>
          <div className="text-white/50">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "-"}</div>
        </div>
      ))}
    </div>
  );
}

/* ----- Referrals ----- */
function ReferralsAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/referrals/admin/list").then((r) => setItems(r.data));
  }, []);
  const totals = items.reduce(
    (a, r) => {
      if (r.status === "rewarded") {
        a.paid += 1;
        a.pointsPaid += (r.customer_credited || 0) + (r.referrer_credited || 0);
      }
      if (r.status === "pending") a.pending += 1;
      return a;
    },
    { paid: 0, pending: 0, pointsPaid: 0 }
  );
  return (
    <div>
      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <div className="border border-white/10 bg-[#0d0d0d] p-4">
          <div className="font-label text-xs text-white/40">REWARDED</div>
          <div className="mt-2 font-display text-3xl font-black text-[#00FFC2]">{totals.paid}</div>
        </div>
        <div className="border border-white/10 bg-[#0d0d0d] p-4">
          <div className="font-label text-xs text-white/40">PENDING</div>
          <div className="mt-2 font-display text-3xl font-black text-[#FFC900]">{totals.pending}</div>
        </div>
        <div className="border border-white/10 bg-[#0d0d0d] p-4">
          <div className="font-label text-xs text-white/40">POINTS PAID OUT</div>
          <div className="mt-2 font-display text-3xl font-black text-[#FF5A1F]">{totals.pointsPaid}</div>
        </div>
      </div>

      <div className="border border-white/10">
        <div className="grid grid-cols-5 gap-2 border-b border-white/10 bg-black px-4 py-3 font-label text-[10px] text-white/40">
          <div>REFERRER</div>
          <div>INVITED</div>
          <div>STATUS</div>
          <div>ORDER</div>
          <div className="text-right">POINTS AWARDED</div>
        </div>
        {items.length === 0 && (
          <div className="p-8 text-center text-sm text-white/40">
            No referrals yet. When a subscriber uses the "Share with friends" widget on the home
            page and their invitee later completes their first order, both parties earn 500 points
            (R50).
          </div>
        )}
        {items.map((r, i) => (
          <div
            key={i}
            className="grid grid-cols-5 items-center gap-2 border-b border-white/10 px-4 py-3 last:border-b-0 text-xs"
          >
            <div className="truncate text-white/80">{r.referrer_email}</div>
            <div className="truncate text-white/80">{r.customer_email}</div>
            <div>
              <span
                className={`inline-block border px-2 py-1 font-label ${
                  r.status === "rewarded"
                    ? "border-[#00FFC2] text-[#00FFC2]"
                    : r.status === "expired"
                    ? "border-white/20 text-white/40"
                    : "border-[#FFC900] text-[#FFC900]"
                }`}
              >
                {r.status}
              </span>
            </div>
            <div className="font-mono text-white/60">{r.order_id || "-"}</div>
            <div className="text-right text-[#FF5A1F]">
              {r.status === "rewarded"
                ? `+${(r.customer_credited || 0) + (r.referrer_credited || 0)}`
                : "-"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----- Blog / Journal admin ----- */
function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/blog/admin/all").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title) return toast.error("Title required");
    try {
      await api.post("/blog", editing);
      toast.success("Post saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };
  const del = async (slug) => {
    if (!confirm(`Delete post "${slug}"?`)) return;
    await api.delete(`/blog/${slug}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="mb-6 flex justify-between">
        <div className="text-xs text-white/40">{posts.length} posts</div>
        <button
          onClick={() => setEditing({ slug: "", title: "", excerpt: "", body: "", cover_image: "", tags: [], published: true })}
          className="inline-flex items-center gap-2 bg-[#FF5A1F] px-4 py-2 font-label text-xs text-black"
        >
          <Plus size={14} weight="bold" /> New post
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 backdrop-blur">
          <div className="flex min-h-full items-start sm:items-center justify-center p-4">
          <div className="w-full max-w-2xl border border-white/10 bg-[#0d0d0d] p-8 my-8">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-2xl font-black">Edit post</div>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <Field label="Slug (URL)"><Input value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} /></Field>
              <Field label="Title"><Input value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} /></Field>
              <Field label="Cover image URL"><Input value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} /></Field>
              <Field label="Excerpt (short teaser)">
                <textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                  rows={2} className="w-full border border-white/15 bg-black px-3 py-2 text-sm" />
              </Field>
              <Field label="Body (Markdown-ish, line breaks preserved)">
                <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  rows={10} className="w-full border border-white/15 bg-black px-3 py-2 text-sm font-mono" />
              </Field>
              <Field label="Published">
                <select value={editing.published ? "1" : "0"} onChange={(e) => setEditing({ ...editing, published: e.target.value === "1" })}
                  className="w-full border border-white/15 bg-black px-3 py-2 text-sm">
                  <option value="1">Published</option><option value="0">Draft</option>
                </select>
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="border border-white/20 px-4 py-2 font-label text-xs">Cancel</button>
              <button onClick={save} className="bg-[#FF5A1F] px-6 py-2 font-label text-xs text-black">Save post</button>
            </div>
          </div>
          </div>
        </div>
      )}

      <div className="border border-white/10">
        <div className="grid grid-cols-4 gap-2 border-b border-white/10 bg-black px-4 py-3 font-label text-[10px] text-white/40">
          <div className="col-span-2">TITLE</div><div>SLUG</div><div className="text-right">ACTIONS</div>
        </div>
        {posts.length === 0 && <div className="p-8 text-center text-sm text-white/40">No posts yet.</div>}
        {posts.map((p) => (
          <div key={p.slug} className="grid grid-cols-4 items-center gap-2 border-b border-white/10 px-4 py-3 last:border-b-0 text-xs">
            <div className="col-span-2 font-display font-bold">{p.title} {!p.published && <span className="ml-2 text-[10px] text-white/40">(draft)</span>}</div>
            <div className="text-white/50">{p.slug}</div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(p)} className="text-white/50 hover:text-[#00FFC2]"><PencilSimple size={16} /></button>
              <button onClick={() => del(p.slug)} className="text-white/50 hover:text-[#FF5A1F]"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----- Bundles admin ----- */
function BundlesAdmin() {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/bundles").then((r) => setBundles(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const save = async () => {
    if (!editing.slug || !editing.name || editing.product_slugs.length < 2) {
      return toast.error("Slug, name & at least 2 products required");
    }
    try {
      await api.post("/bundles", { ...editing, bundle_price: Number(editing.bundle_price) || 0 });
      toast.success("Bundle saved");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (slug) => {
    if (!confirm(`Delete bundle ${slug}?`)) return;
    await api.delete(`/bundles/${slug}`);
    load();
  };
  const toggleProd = (slug) => {
    const list = editing.product_slugs.includes(slug)
      ? editing.product_slugs.filter((s) => s !== slug)
      : [...editing.product_slugs, slug];
    setEditing({ ...editing, product_slugs: list });
  };

  return (
    <div>
      <div className="mb-6 flex justify-between">
        <div className="text-xs text-white/40">{bundles.length} bundles</div>
        <button
          onClick={() => setEditing({ slug: "", name: "", description: "", product_slugs: [], bundle_price: 0, active: true })}
          className="inline-flex items-center gap-2 bg-[#FF5A1F] px-4 py-2 font-label text-xs text-black"
        >
          <Plus size={14} weight="bold" /> New bundle
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 backdrop-blur">
          <div className="flex min-h-full items-start sm:items-center justify-center p-4">
          <div className="w-full max-w-2xl border border-white/10 bg-[#0d0d0d] p-8 my-8">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-2xl font-black">Edit bundle</div>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <Field label="Slug"><Input value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} /></Field>
              <Field label="Name"><Input value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} /></Field>
              <Field label="Description">
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={2} className="w-full border border-white/15 bg-black px-3 py-2 text-sm" />
              </Field>
              <Field label="Bundle price (R)"><Input type="number" value={editing.bundle_price} onChange={(v) => setEditing({ ...editing, bundle_price: v })} /></Field>
              <Field label="Products in this bundle">
                <div className="max-h-56 overflow-y-auto border border-white/10 bg-black p-2 space-y-1">
                  {products.map((p) => (
                    <label key={p.slug} className="flex items-center gap-2 px-2 py-1 hover:bg-white/5 cursor-pointer text-xs">
                      <input type="checkbox" checked={editing.product_slugs.includes(p.slug)} onChange={() => toggleProd(p.slug)} />
                      <span className="flex-1">{p.name}</span>
                      <span className="text-white/40">R {p.price.toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="border border-white/20 px-4 py-2 font-label text-xs">Cancel</button>
              <button onClick={save} className="bg-[#FF5A1F] px-6 py-2 font-label text-xs text-black">Save bundle</button>
            </div>
          </div>
          </div>
        </div>
      )}

      <div className="border border-white/10">
        <div className="grid grid-cols-6 gap-2 border-b border-white/10 bg-black px-4 py-3 font-label text-[10px] text-white/40">
          <div className="col-span-2">NAME</div><div>ITEMS</div><div>MSRP</div><div>PRICE</div><div className="text-right">ACTIONS</div>
        </div>
        {bundles.length === 0 && <div className="p-8 text-center text-sm text-white/40">No bundles yet.</div>}
        {bundles.map((b) => (
          <div key={b.slug} className="grid grid-cols-6 items-center gap-2 border-b border-white/10 px-4 py-3 last:border-b-0 text-xs">
            <div className="col-span-2 font-display font-bold">{b.name}</div>
            <div>{b.product_slugs?.length}</div>
            <div className="text-white/50">R {(b.msrp_total || 0).toFixed(2)}</div>
            <div className="text-[#00FFC2]">R {b.bundle_price.toFixed(2)}</div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(b)} className="text-white/50 hover:text-[#00FFC2]"><PencilSimple size={16} /></button>
              <button onClick={() => del(b.slug)} className="text-white/50 hover:text-[#FF5A1F]"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
